import { Link } from "react-router-dom";
import { ShieldCheck, CreditCard, ArrowUpRight, Lock, CheckCircle2, FileText, AlertCircle } from "lucide-react";
import SectionHeading from "../components/SectionHeading";
import { MaskedLines, FadeIn, Reveal } from "../components/motion/Reveal";
import PageMeta from "../components/PageMeta";
import { useSettings } from "../context/SettingsContext";

const TERMS_SECTIONS = [
  {
    number: "01",
    title: "Commercial Payment Terms & Advance Deposit",
    content: `All production and bulk OEM/ODM export orders are subject to our verified manufacturing payment schedule:
• 50% Advance Deposit: Required upon official Proforma Invoice (PI) confirmation and counter-signing. Production scheduling, raw material procurement (T700 carbon fiber, precision honeycomb, custom fabric rolls), and tooling begin immediately upon receipt of deposit.
• 50% Balance Payment: Payable upon completion of mass production, multi-point QC inspection report sign-off, and prior to final dispatch or container departure.
• Sample & Proto Orders: 100% advance payment required for custom prototype development, Pantone lab-dip strikes, and physical sample production before dispatch.`,
  },
  {
    number: "02",
    title: "Accepted International Payment Methods",
    content: `We accept official, certified international commercial payment gateways for worldwide B2B export:
1. Bank Wire Transfer (T/T - Telegraphic Transfer): Direct SWIFT / IBAN wire to our verified corporate export bank account in USD ($), EUR (€), or GBP (£).
2. Western Union & MoneyGram: Fast commercial wire transfer for rapid sample orders, rush tooling deposits, and accelerated production starts.
3. Irrevocable Letter of Credit (L/C at Sight): Available for full container load (FCL) orders and large commercial government / institutional contracts upon prior credit agreement.
4. Digital Business Transfers: Wise (TransferWise), Payoneer, Remitly, and Ria Money Transfer for fast settlement and reduced cross-border foreign exchange conversion fees.
5. PayPal: Express prototype sampling, urgent courier dispatch fees, and pre-production sample charges.`,
  },
  {
    number: "03",
    title: "Proforma Invoice (PI) & Order Confirmation",
    content: `Every export order is formalized with an official Proforma Invoice (PI) detailing:
• Exact technical product specifications (materials, core thickness, dimensions, surface texture, weight tolerance).
• Fabric GSM, color Pantone references (TCX/TPX), size breakdowns, and customization placements.
• Unit pricing, total quantity, delivery terms (FOB Karachi, CIF destination port, or DDP air/sea door delivery), and agreed production timeline.
• Official corporate bank beneficiary coordinates with SWIFT code and IBAN.`,
  },
  {
    number: "04",
    title: "Production Timeline & Lead Times",
    content: `Standard mass production timelines range from 20 to 35 business days from the date the 50% advance deposit is cleared and all pre-production artwork / samples are approved. Expedited production schedules can be arranged upon agreement. Lead times do not include transit duration, which varies based on air freight (5–10 days) or sea freight (20–45 days).`,
  },
  {
    number: "05",
    title: "Quality Control & Pre-Shipment Inspection",
    content: `We adhere to international AQL 2.5 quality standards. Prior to final balance payment and shipment dispatch, clients receive comprehensive high-definition video inspection footage, batch test metrics, weight/balance measurements, and high-resolution packaging photos. Third-party inspection agencies (e.g. SGS, Intertek, Bureau Veritas) are welcomed at our Sialkot manufacturing plant prior to release.`,
  },
  {
    number: "06",
    title: "Shipping, Incoterms & Export Documentation",
    content: `We provide complete export documentation for smooth customs clearance at your destination port, including Commercial Invoices, Detailed Packing Lists, Certificate of Origin, and Bills of Lading (BL / Air Waybill). Standard shipping terms include FOB (Free On Board), CIF (Cost, Insurance & Freight), and DDP (Delivered Duty Paid).`,
  },
  {
    number: "07",
    title: "Intellectual Property & Non-Disclosure (NDA)",
    content: `We enforce strict confidentiality on all custom private label designs, vector artwork, brand molds, and proprietary specifications. Your brand molds, proprietary handle tooling, and custom graphics will never be shared, marketed, or sold to third parties. Bilateral Non-Disclosure Agreements (NDAs) are provided upon request prior to tech-pack submission.`,
  },
  {
    number: "08",
    title: "Warranty, Defect Policy & Claims",
    content: `We stand behind our craftsmanship. Any manufacturing defect verified upon receipt (reported within 14 days of port clearance with photo/video evidence) will be credited on subsequent orders or replaced in priority production runs at no charge.`,
  },
];

const TermsAndConditions = () => {
  const site = useSettings();

  return (
    <div data-testid="terms-page">
      <PageMeta
        title="Terms & Conditions — Commercial Export & Payment Terms"
        description="Official B2B commercial export terms, 50% advance payment policy, accepted international payment methods (T/T Bank Wire, Western Union, MoneyGram, L/C), and quality guarantees for Pickleballgearz."
      />

      {/* Hero Header */}
      <section className="border-b border-black/10 bg-white">
        <div className="mx-auto max-w-[1400px] px-5 pb-16 pt-16 md:pt-24 lg:px-10">
          <FadeIn>
            <p className="mono-caption mb-8 text-neutral-500">Commercial Policy & Terms of Service — Updated 2026</p>
          </FadeIn>
          <h1 className="font-display text-4xl font-semibold leading-[1.02] tracking-tighter sm:text-5xl lg:text-6xl">
            <MaskedLines lines={["TERMS &", <>CONDITIONS<span className="text-brand-red">.</span></>]} />
          </h1>
        </div>
      </section>

      {/* Main Content */}
      <section className="mx-auto max-w-[1100px] px-5 py-20 md:py-28 lg:px-10">
        {/* Payment Summary Box */}
        <Reveal>
          <div className="mb-14 border border-brand-ink bg-brand-ink p-6 sm:p-8 text-white">
            <div className="flex items-center gap-2">
              <span className="bg-brand-red px-2.5 py-0.5 font-mono text-[10px] font-bold uppercase tracking-wider text-white">
                Official Payment Protocol
              </span>
              <span className="font-mono text-xs text-white/60">Export Standards</span>
            </div>
            <h2 className="mt-4 font-display text-2xl font-medium tracking-tight sm:text-3xl">
              50% Advance Deposit · 50% Balance on Pre-Shipment Inspection
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-white/70">
              To ensure top-tier material sourcing, dedicated machine queueing, and timely delivery, all production orders require a <strong className="text-white">50% advance deposit</strong> upon signing the Proforma Invoice (PI), with the remaining <strong className="text-white">50% balance</strong> due upon final QC inspection approval prior to shipping.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/inquiry" className="btn-primary !h-9 !px-4 !text-xs">
                Request a Quote <ArrowUpRight size={14} />
              </Link>
              <Link to="/contact" className="btn-outline !h-9 !border-white/30 !px-4 !text-xs !text-white hover:!bg-white hover:!text-brand-ink">
                Contact Export Team
              </Link>
            </div>
          </div>
        </Reveal>

        <SectionHeading number="01" title="Commercial & Manufacturing Policies" sub="Clear, transparent terms governing all OEM/ODM manufacturing relationships." />

        {/* Detailed Terms Accordion / Sections */}
        <div className="divide-y divide-black/10 border-y border-black/10">
          {TERMS_SECTIONS.map((sec, i) => (
            <Reveal key={sec.title} delay={i * 0.04}>
              <div className="grid gap-4 py-8 sm:grid-cols-12" data-testid={`terms-section-${i}`}>
                <span className="font-mono text-sm text-brand-red sm:col-span-1">{sec.number}</span>
                <h3 className="font-display text-xl font-medium tracking-tight sm:col-span-4">{sec.title}</h3>
                <div className="sm:col-span-7">
                  <p className="text-sm leading-relaxed text-neutral-600 whitespace-pre-line">
                    {sec.content}
                  </p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        {/* Contact info for billing */}
        <div className="mt-14 border border-black/10 bg-brand-smoke p-6">
          <div className="flex items-start gap-3">
            <AlertCircle size={20} className="shrink-0 text-brand-red mt-0.5" />
            <div>
              <h4 className="font-display text-base font-semibold text-brand-ink">Need custom commercial billing terms?</h4>
              <p className="mt-1 text-xs text-neutral-600 leading-relaxed">
                For large volume annual contracts, distributor agreements, or government tenders, contact our finance and export department at <a href={`mailto:${site.email}`} className="font-semibold text-brand-red underline">{site.email}</a> or call <a href={site.phoneLink} className="font-semibold text-brand-red underline">{site.phone}</a>.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TermsAndConditions;
